import { useCallback, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import REQ, { ReqType } from '../../../../utils/REQ';
import {
  useAppDispatch,
  useAppSelector
} from '../../../../_common/hooks/reduxHooks';
import { postProducerCreateCustomer } from '../../../../api';
import { fetchConsumers } from '../../../reducers/consumers';
import { AddCustomerFormValues } from '../types';

const formValuesToCustomer = (
  formValues: AddCustomerFormValues,
  userId: string
) => {
  if (
    !formValues.organization ||
    !formValues.organization.name ||
    !formValues.organization.orgNumber ||
    !formValues.organization.address ||
    !formValues.billingName ||
    !formValues.primaryEmail ||
    !formValues.reminderEmail ||
    !formValues.billingAddress ||
    typeof formValues.billingAddress == 'string' ||
    !formValues.customerName ||
    !formValues.contactPersonName ||
    !formValues.contactPhone ||
    !formValues.notificationEmail ||
    !formValues.deliveryAddress ||
    typeof formValues.deliveryAddress === 'string' ||
    !formValues.deliveryWindowFrom ||
    !formValues.deliveryWindowTo
  ) {
    return null;
  }

  return {
    createdBy: userId,
    organizationName: formValues.organization.name,
    organizationNumber: formValues.organization.orgNumber,
    organizationAddress: formValues.organization.address,
    isVatRegistered: !formValues.organization.isNotVatRegistered,
    billingAccountName: formValues.billingName,
    billingDepartmentName: formValues.department || undefined,
    billingEanNumber: formValues.eanNumber || undefined,
    billingPrimaryInvoiceEmail: formValues.primaryEmail,
    billingReminderInvoiceEmail: formValues.reminderEmail,
    billingAddress: formValues.billingAddress,
    contactInformationCustomerName: formValues.customerName,
    contactInformationContactPersonFullName: formValues.contactPersonName,
    contactInformationContactPhone: formValues.contactPhone,
    contactInformationNotificationEmail: formValues.notificationEmail,
    deliveryAddress: {
      addressLine1: formValues.deliveryAddress.addressLine1,
      addressLine2:
        !formValues.deliveryAddress.addressLine2 ||
        formValues.deliveryAddress.addressLine2 === ''
          ? undefined
          : formValues.deliveryAddress.addressLine2,
      postalCode: formValues.deliveryAddress.postalCode,
      city: formValues.deliveryAddress.city,
      country: formValues.deliveryAddress.country
    },
    deliveryWindow: {
      start: formValues.deliveryWindowFrom,
      end: formValues.deliveryWindowTo
    },
    deliveryInstructions: formValues.deliveryInstructions || undefined
  };
};

// Navigate to next step and pass the form value
export const useOnSave = () => {
  const navigate = useNavigate();
  const [saveReq, setSaveReq] = useState<ReqType>(REQ.INIT);
  const dispatch = useAppDispatch();
  const { producerId, userId } = useAppSelector(({ auth }) => {
    return { producerId: auth._id, userId: auth.uid };
  });

  const onSave = useCallback(async (data: AddCustomerFormValues) => {
    setSaveReq(REQ.PENDING);
    if (!producerId || !userId) {
      setSaveReq(REQ.ERROR);
      return;
    }

    try {
      const customer = formValuesToCustomer(data, userId);
      if (!customer) {
        setSaveReq(REQ.ERROR);
        return;
      }
      await postProducerCreateCustomer(producerId, customer);
      dispatch(fetchConsumers({ producerId }));
      setSaveReq(REQ.SUCCESS);

      const search = data.customerName
        ? createSearchParams({
            query: data.customerName
          }).toString()
        : undefined;
      setTimeout(() => {
        navigate(`/customers?${search}`);
      }, 1000);
    } catch (error) {
      setSaveReq(REQ.ERROR);
    }
  }, []);

  return { onSave, saveReq };
};
